import { exportFontKey, type ExportFontKey } from '@peruselab/structure-editor/rendering/textOutlines';

// #758 part 2 -- vector PDF from the exact SVG the export preview shows, via jsPDF + svg2pdf.js
// (owner decision, docs/model/drawing-ui-export.md). Both load only when a PDF is exported.
//
// Fonts: text boxes are already outlined (#757); what stays live text (atom labels and charges,
// characters the outline fonts lack) is drawn with the bundled open fonts, embedded under the
// family names the SVG uses -- accepted "PDF embeds fonts where permitted" (all are SIL OFL).
// Without them svg2pdf would fall back to PDF's built-in Helvetica, which lacks e.g. "₂".

const STYLES = [
  { style: 'normal', bold: false, italic: false },
  { style: 'bold', bold: true, italic: false },
  { style: 'italic', bold: false, italic: true },
  { style: 'bolditalic', bold: true, italic: true }
] as const;

function base64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binary);
}

// The first family of every font-family list in the SVG that still draws live text, as svg2pdf
// looks it up (unquoted). #792: labels are outlined, so their group's font-family alone (with only
// paths inside) needs no embedded font.
function familiesIn(root: Element): Set<string> {
  const families = new Set<string>();
  for (const element of root.querySelectorAll('[font-family]')) {
    const drawsText = element.localName === 'text' || element.localName === 'tspan' || element.querySelector('text');
    if (!drawsText) continue;
    const first = (element.getAttribute('font-family') ?? '')
      .split(',')[0]
      ?.trim()
      .replace(/^["']|["']$/g, '');
    if (first) families.add(first);
  }
  return families;
}

// svg2pdf maps only 400/700 onto regular/bold; atom labels use 600 and charges 800, which would
// otherwise miss the embedded fonts. In the PDF's copy only, heavy weights become bold.
function normaliseWeights(root: Element): void {
  for (const element of root.querySelectorAll('[font-weight]')) {
    const weight = Number(element.getAttribute('font-weight'));
    if (Number.isFinite(weight)) element.setAttribute('font-weight', weight >= 600 ? 'bold' : 'normal');
  }
}

export async function svgToPdf(
  svg: string,
  sizeMm: { readonly width: number; readonly height: number },
  fontBaseUrl: string
): Promise<Blob> {
  const [{ jsPDF }, { svg2pdf }] = await Promise.all([import('jspdf'), import('svg2pdf.js')]);
  const root = new DOMParser().parseFromString(svg, 'image/svg+xml').documentElement;
  normaliseWeights(root);

  const { width, height } = sizeMm;
  const pdf = new jsPDF({
    unit: 'mm',
    format: [width, height],
    orientation: width > height ? 'landscape' : 'portrait',
    compress: true,
    putOnlyUsedFonts: true
  });

  const loaded = new Map<ExportFontKey, Promise<string>>();
  const fontData = (key: ExportFontKey) => {
    if (!loaded.has(key)) {
      loaded.set(
        key,
        fetch(`${fontBaseUrl.replace(/\/$/, '')}/${key}.ttf`).then((response) => {
          if (!response.ok) throw new Error(`Could not load export font ${key} (${response.status})`);
          return response.arrayBuffer().then(base64);
        })
      );
    }
    return loaded.get(key)!;
  };
  for (const family of familiesIn(root)) {
    for (const { style, bold, italic } of STYLES) {
      const key = exportFontKey({ fontFamily: family, bold, italic });
      const file = `${family}-${style}.ttf`;
      pdf.addFileToVFS(file, await fontData(key));
      pdf.addFont(file, family, style, undefined, 'Identity-H');
    }
  }

  await svg2pdf(root, pdf, { x: 0, y: 0, width, height });
  return pdf.output('blob');
}
